import { zodResolver } from '@hookform/resolvers/zod';
import { CreateTenantSchema, type CreateTenant } from '@rfleet/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Badge, Dialog, EmptyState, Field, PageHeader, Pagination, Skeleton, Spinner, Table, Td, Th, useToast } from '../../components/ui';
import { api, qs, type Paginated } from '../../lib/api';

export interface TenantRow {
  id: string;
  slug: string;
  nameAr: string;
  nameEn: string | null;
  status: 'ACTIVE' | 'SUSPENDED' | 'TRIAL' | 'CLOSED';
  createdAt: string;
  subscription: { status: string; plan: { code: string; nameAr: string; nameEn: string; maxVehicles: number } } | null;
  _count: { vehicles: number; users: number; devices: number };
}
interface Plan { code: string; nameAr: string; nameEn: string }

export const statusTone = (s: TenantRow['status']): 'success' | 'info' | 'warning' | 'neutral' => (s === 'ACTIVE' ? 'success' : s === 'TRIAL' ? 'info' : s === 'SUSPENDED' ? 'warning' : 'neutral');

export function TenantsPage() {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const q = useQuery({ queryKey: ['super-tenants', page, search], queryFn: () => api<Paginated<TenantRow>>(`/super/tenants${qs({ page, q: search })}`) });
  const name = (r: { nameAr: string; nameEn: string | null }) => (i18n.language === 'en' && r.nameEn ? r.nameEn : r.nameAr);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('admin.tenants')} actions={<button className="btn-primary" onClick={() => setCreating(true)}><Plus size={16} aria-hidden /> {t('admin.addTenant')}</button>} />
      <input className="input max-w-sm" placeholder={t('common.search')} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} aria-label={t('common.search')} />
      {q.isLoading ? <Skeleton className="h-64" /> : !q.data?.data.length ? <div className="card"><EmptyState title={t('common.empty')} /></div> : (
        <Table>
          <thead><tr><Th>{t('admin.tenant')}</Th><Th>slug</Th><Th>{t('admin.status')}</Th><Th>{t('admin.plan')}</Th><Th>{t('nav.vehicles')}</Th><Th>{t('settings.tabs.users')}</Th><Th>{t('admin.devices')}</Th></tr></thead>
          <tbody>
            {q.data.data.map((r) => (
              <tr key={r.id} className="hover:bg-surface-hover">
                <Td className="font-semibold"><Link to={`/admin/tenants/${r.id}`} className="text-brand-ink hover:underline">{name(r)}</Link></Td>
                <Td><span dir="ltr" className="text-text-muted">{r.slug}</span></Td>
                <Td><Badge tone={statusTone(r.status)} dot>{t(`admin.statuses.${r.status}`)}</Badge></Td>
                <Td>{r.subscription ? name(r.subscription.plan) : '—'}</Td>
                <Td>{r._count.vehicles}{r.subscription ? <span className="text-text-faint"> / {r.subscription.plan.maxVehicles}</span> : null}</Td>
                <Td>{r._count.users}</Td>
                <Td>{r._count.devices}</Td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr><td colSpan={7}><Pagination page={q.data.meta.page} pageSize={q.data.meta.pageSize} total={q.data.meta.total} onPage={setPage} /></td></tr></tfoot>
        </Table>
      )}
      {creating ? <CreateTenantDialog onClose={() => setCreating(false)} /> : null}
    </div>
  );
}

function CreateTenantDialog({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const plans = useQuery({ queryKey: ['super-plans'], queryFn: () => api<Plan[]>('/super/plans') });
  const form = useForm<CreateTenant>({ resolver: zodResolver(CreateTenantSchema), defaultValues: { timezone: 'Africa/Cairo', locale: 'AR', trialDays: 14, planCode: 'starter', admin: { email: '', fullName: '', password: '' } } });
  const save = useMutation({
    mutationFn: (v: CreateTenant) => api('/super/tenants', { method: 'POST', json: v }),
    onSuccess: () => { toast.success(t('common.saved')); void qc.invalidateQueries({ queryKey: ['super-tenants'] }); onClose(); },
    onError: toast.error,
  });
  const e = form.formState.errors;
  return (
    <Dialog open onClose={onClose} title={t('admin.addTenant')} size="lg" footer={<><button className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button><button className="btn-primary" form="tenant-form" type="submit" disabled={save.isPending}>{save.isPending ? <Spinner /> : null}{t('common.save')}</button></>}>
      <form id="tenant-form" onSubmit={form.handleSubmit((v) => save.mutate(v))} className="grid gap-4 sm:grid-cols-2" noValidate>
        <Field label={t('settings.company.nameAr')} required error={e.nameAr?.message}>{(id) => <input id={id} className="input" {...form.register('nameAr')} />}</Field>
        <Field label={t('settings.company.nameEn')} error={e.nameEn?.message}>{(id) => <input id={id} dir="ltr" className="input" {...form.register('nameEn')} />}</Field>
        <Field label="slug" required error={e.slug?.message} hint={t('admin.slugHint')}>{(id) => <input id={id} dir="ltr" className="input" {...form.register('slug')} />}</Field>
        <Field label={t('admin.plan')} required error={e.planCode?.message}>{(id) => <select id={id} className="input" {...form.register('planCode')}>{plans.data?.map((p) => <option key={p.code} value={p.code}>{i18n.language === 'en' ? p.nameEn : p.nameAr}</option>)}</select>}</Field>
        <Field label={t('admin.trialDays')} error={e.trialDays?.message}>{(id) => <input id={id} type="number" dir="ltr" className="input" {...form.register('trialDays', { valueAsNumber: true })} />}</Field>
        <Field label={t('settings.company.locale')}>{(id) => <select id={id} className="input" {...form.register('locale')}><option value="AR">العربية</option><option value="EN">English</option></select>}</Field>
        <h3 className="col-span-full mt-2 text-sm font-bold">{t('admin.firstAdmin')}</h3>
        <Field label={t('settings.users.name')} required error={e.admin?.fullName?.message}>{(id) => <input id={id} className="input" {...form.register('admin.fullName')} />}</Field>
        <Field label={t('login.email')} required error={e.admin?.email?.message}>{(id) => <input id={id} type="email" dir="ltr" className="input" {...form.register('admin.email')} />}</Field>
        <Field label={t('login.password')} required error={e.admin?.password?.message}>{(id) => <input id={id} type="password" dir="ltr" className="input" autoComplete="new-password" {...form.register('admin.password')} />}</Field>
      </form>
    </Dialog>
  );
}
